import { signIn, signOut, resetPassword, confirmResetPassword, signUp, setUpTOTP, verifyTOTPSetup, updateMFAPreference, confirmSignIn } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { adminAddCognitoUserToAdminGroup, adminCreateCognitoUser, adminDeleteCognitoUser, adminRemoveCognitoUserFromAdminGroup, adminSetCognitoUserPassword, adminSetUserMFA, createUser, deleteUser, updateUser, handleUserDisplayOrder } from 'graphql/mutations';
import { listUsers, getUserByEmail, getUser } from 'graphql/queries';
import { AUTH_MODE } from 'constants/config';
import { convertDateTimeFormat, convertMomentFormat } from 'utils/function';
import { AccountType } from 'models';
import { apiDeleteUserDeviceGroups } from './UserDeviceGroups';
import { v4 as uuidv4 } from 'uuid';
const client = generateClient();

export const loginCognitoUser = async (email, password) => {
  try {
    const res = await signIn({ username: email.trim(), password });
    return {
      success: true,
      data: res
    };
  } catch (e) {
    return { success: false, error: e }
  }
}

export async function signInConfirmation(totpCode) {
  try {
    const res = await confirmSignIn({ challengeResponse: totpCode });
    return {
      success: true,
      data: res
    };
  } catch (error) {
    return {
      success: false,
      error: error
    }
  }
}

export async function handleTOTPSetup(appName, accountName) {
  try {
    const totpSetupDetails = await setUpTOTP();
    const setupUri = totpSetupDetails.getSetupUri(appName, accountName);
    // Open setupUri with an authenticator APP to retrieve an OTP code
    return {
      success: true,
      data: setupUri.toString()
    }
  } catch (error) {
    console.log(error);
    return {
      success: false,
      error: error
    }
  }
}

export async function handleTOTPSetupVerification(totpCode) {
  try {
    await verifyTOTPSetup({ code: totpCode });
    return { success: true };
  } catch (error) {
    console.log(error);
    return { success: false, error: error };
  }
}

export async function handleUpdateMFAPreference() {
  try {
    await updateMFAPreference({ totp: 'PREFERRED' });
  } catch (error) {
    console.log(error);
  }
}

export const logoutCognitoUser = async () => {
  try {
    await signOut()
  } catch (e) {
    console.error(e);
  }
}

export const sendVerificationCodeForgotPassword = async (username) => {
  return await resetPassword({ username })
    .then((data) => { return { status: 1, message: data } })
    .catch((err) => { return { status: 0, message: err } });
}

export const submitNewPasswordWithVerificationCode = async (username, code, newPassword) => {
  return await confirmResetPassword({ username, confirmationCode: code, newPassword })
    .then((data) => { return { status: 1, message: data } })
    .catch((err) => { return { status: 0, message: err } });
}

const _apiAdminCreateCognitoUser = async (input) => {
  try {
    const response = await client.graphql({
      query: adminCreateCognitoUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });
    return response.data?.adminCreateCognitoUser;
  } catch (e) {
    return {
      success: false,
      error: e
    };
  }
}

export const createCognitoUser = async (data) => {
  try {
    const response = await signUp({
      username: data.email,
      password: data.password,
      attributes: {
        email: data.email,
        name: data.name
      },
      autoSignIn: {
        enabled: false
      }
    })
    return response;
  } catch (e) {
    // console.error("ERROR", e);
  }
}

export const apiListUsers = async (nextToken = null) => {
  try {
    const response = await client.graphql({
      query: listUsers,
      variables: {
        nextToken
      },
      authMode: AUTH_MODE
    });
    const users = response.data?.listUsers?.items;
    for (const user of users) {
      user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
      user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
      if (user.data) {
        user.data = JSON.parse(user.data);
      }
    }
    return {
      success: true,
      data: users
    };
  } catch (e) {
    return {
      success: false,
      error: e
    };
  }
}

export const apiGetUserByEmail = async (email) => {
  try {
    const response = await client.graphql({
      query: getUserByEmail,
      variables: {
        email
      },
      authMode: AUTH_MODE
    });
    const user = response.data?.getUserByEmail?.items[0];
    if (user?.createdAt) user.createdAt = convertDateTimeFormat(user.createdAt);
    if (user?.updatedAt) user.updatedAt = convertDateTimeFormat(user.updatedAt);
    if (user?.data) {
      user.data = JSON.parse(user.data);
    }
    return user;
  } catch (e) {
    console.log("🚀 ~ apiGetUserByEmail ~ e:", e);
    return null;
  }
}

export const apiCreateUser = async (input) => {
  try {
    // Prepare user data with display order
    let userData = {};
    if (input.data) {
      userData = typeof input.data === 'string' ? JSON.parse(input.data) : input.data;
    }

    // Assign the next available display order for new users
    if (!userData.displayOrder) {
      userData.displayOrder = await getNextDisplayOrder();
    }

    const createResult = await client.graphql({
      query: createUser,
      variables: {
        input: {
          id: uuidv4(),
          name: input.name,
          email: input.email,
          accountType: input.accountType,
          contact: input.contact,
          phone: input.phone,
          enabledMFA: input.enabledMFA,
          description: input.description,
          data: JSON.stringify(userData),
        }
      },
      authMode: AUTH_MODE
    });
    const createData = createResult.data?.createUser;
    const cognitoResult = await _apiAdminCreateCognitoUser({
      type: 'createUser',
      userId: createData.id,
      name: input.name,
      email: input.email,
      password: input.password,
    })
    if (cognitoResult.success) {
      if (input.accountType === AccountType.ADMIN || input.accountType === AccountType.MANAGER) {
        const changeRoleResult = await _addUserToAdminGroup(input.email);
        if (!changeRoleResult.success) return changeRoleResult;
      }
    }
    const updateResult = await apiUpdateUser({ id: createData.id, isActive: true });
    if (!updateResult.success) return updateResult;
    if (createData.updatedAt) createData.updatedAt = convertMomentFormat(createData.updatedAt);
    if (createData.createdAt) createData.createdAt = convertMomentFormat(createData.createdAt);
    if (createData.data) {
      createData.data = JSON.parse(createData.data);
    }
    return {
      success: true,
      data: createData
    };
  } catch (e) {
    console.log("🚀 ~ apiCreateUser ~ e:", e)
    return {
      success: false,
      error: e
    };
  }
}

const _addUserToAdminGroup = async (email) => {
  try {
    const response = await client.graphql({
      query: adminAddCognitoUserToAdminGroup,
      variables: {
        input: {
          type: 'addUserToAdminGroup',
          email
        }
      },
      authMode: AUTH_MODE
    });
    return response.data.adminAddCognitoUserToAdminGroup;
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const _removeUserFromAdminGroup = async (email) => {
  try {
    const response = await client.graphql({
      query: adminRemoveCognitoUserFromAdminGroup,
      variables: {
        input: {
          type: 'removeUserFromAdminGroup',
          email
        }
      },
      authMode: AUTH_MODE
    });
    return response.data.adminRemoveCognitoUserFromAdminGroup;
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const _setUserMFA = async (email, enabledMFA) => {
  try {
    const response = await client.graphql({
      query: adminSetUserMFA,
      variables: {
        input: {
          type: 'setUserMFA',
          email,
          enabledMFA
        }
      },
      authMode: AUTH_MODE
    });
    return response.data.adminSetUserMFA;
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiUpdateUser = async (input) => {
  try {
    if (input.data) {
      input.data = JSON.stringify(input.data);
    }
    if (input.accountType) {
      const changeRoleResult = input.accountType === AccountType.USER ? await _removeUserFromAdminGroup(input.email) : await _addUserToAdminGroup(input.email);
      if (!changeRoleResult.success) {
        return changeRoleResult;
      }
    }
    if (input.enabledMFA !== undefined || input.enabledMFA !== null) {
      await _setUserMFA(input.email, input.enabledMFA);
    }

    const response = await client.graphql({
      query: updateUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });
    const user = response.data?.updateUser;
    user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
    user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
    if (user?.data) {
      user.data = JSON.parse(user.data);
    }
    return {
      success: true,
      data: user
    }
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiUpdateDataUser = async(input) => {
  try {
    if (input.data) {
      input.data = JSON.stringify(input.data);
    }

    const response = await client.graphql({
      query: updateUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });

    const user = response.data?.updateUser;

    return {
      success: true,
      data: user
    }
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiDeleteUser = async (userId, userEmail) => {
  try {
    let response = await client.graphql({
      query: adminDeleteCognitoUser,
      variables: {
        input: {
          type: 'deleteUser',
          email: userEmail
        }
      },
      authMode: AUTH_MODE
    });
    const adminDeleteResult = response.data.adminDeleteCognitoUser;
    if (!adminDeleteResult.success) {
      return adminDeleteResult;
    }

    await apiDeleteUserDeviceGroups(userId); // Delete user device groups if any

    const deleteResult = await client.graphql({
      query: deleteUser,
      variables: {
        input: {
          id: userId
        }
      },
      authMode: AUTH_MODE
    });

    // Compact display orders after successful deletion
    // This removes gaps in the display order sequence
    await compactDisplayOrders(userId);

    return {
      success: true,
      data: deleteResult.data.deleteUser
    };
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiGetUser = async (userId) => {
  try {
    const res = await client.graphql({
      query: getUser,
      variables: {
        id: userId
      },
      authMode: AUTH_MODE
    });
    const user = res.data?.getUser;
    if (user?.createdAt) user.createdAt = convertDateTimeFormat(user.createdAt);
    if (user?.udatedAt) user.udatedAt = convertDateTimeFormat(user.updatedAt);
    if (user?.data) {
      user.data = JSON.parse(user.data);
    }
    return {
      success: true,
      data: user
    };
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiAdminChangeUserPassword = async (userEmail, password) => {
  try {
    const response = await client.graphql({
      query: adminSetCognitoUserPassword,
      variables: {
        input: {
          type: 'setUserPassword',
          email: userEmail,
          password: password
        }
      },
      authMode: AUTH_MODE
    });
    return {
      success: true,
      data: response.data.adminSetCognitoUserPassword
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiUpdateUserLastTimeLogin = async (input) => {
  try {
    const response = await client.graphql({
      query: updateUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });
    const user = response.data?.updateUser;
    user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
    user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
    return {
      success: true,
      data: user,
      response: response
    }
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

/**
 * Update display order for multiple users
 * @param {Array} userOrderData - Array of objects with {id, displayOrder}
 * @returns {Object} API response
 */
export const apiUpdateUsersDisplayOrder = async (userOrderData) => {
  try {
    // Use the Lambda function for batch processing
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'batchUpdateDisplayOrder',
          userOrderData: userOrderData
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || 'Unknown error',
        message: 'Failed to update users display order'
      };
    }

    // Format the response data
    const formattedUsers = result.updatedUsers?.map(user => {
      if (user) {
        user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
        user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
        if (user?.data) {
          try {
            user.data = typeof user.data === 'string' ? JSON.parse(user.data) : user.data;
          } catch (e) {
            console.warn('Error parsing user data:', e);
          }
        }
      }
      return user;
    }) || [];

    return {
      success: true,
      data: formattedUsers,
      message: `Successfully updated display order for ${formattedUsers.length} users`
    };

  } catch (error) {
    console.error('Error updating users display order:', error);
    return {
      success: false,
      error: error,
      message: 'Failed to update users display order'
    };
  }
};

/**
 * Get users sorted by display order
 * @param {string} nextToken - Pagination token
 * @returns {Object} API response with sorted users
 */
export const apiListUsersSortedByDisplayOrder = async () => {
  try {
    const response = await apiListUsers();

    if (!response.success) {
      return response;
    }

    // Check if any users don't have displayOrder and assign them
    const usersNeedingOrder = response.data.filter(user =>
      !user.data?.displayOrder && user.data?.displayOrder !== 0
    );

    if (usersNeedingOrder.length > 0) {
      // Find the highest existing display order
      const maxOrder = response.data.reduce((max, user) => {
        const order = user.data?.displayOrder || 0;
        return Math.max(max, order);
      }, 0);

      // Assign display orders to users that don't have them
      const orderUpdates = usersNeedingOrder.map((user, index) => ({
        id: user.id,
        displayOrder: maxOrder + index + 1
      }));

      // Update these users with display orders
      await apiUpdateUsersDisplayOrder(orderUpdates);

      // Refetch the updated data
      const updatedResponse = await apiListUsers();
      if (updatedResponse.success) {
        response.data = updatedResponse.data;
      }
    }

    // Sort users by display order
    const sortedUsers = response.data.sort((a, b) => {
      const orderA = a.data?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.data?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    return {
      ...response,
      data: sortedUsers
    };

  } catch (error) {
    console.error('Error listing users sorted by display order:', error);
    return {
      success: false,
      error: error,
      message: 'Failed to list users sorted by display order'
    };
  }
};

/**
 * Get the next available display order for a new user
 * @returns {Promise<number>} Next display order number
 */
const getNextDisplayOrder = async () => {
  try {
    // Use the Lambda function for efficient next order calculation
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'getNextDisplayOrder'
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (result?.success) {
      const data = JSON.parse(result.data);
      return data.nextDisplayOrder;
    }

    return 1; // Default to 1 if Lambda function fails
  } catch (error) {
    console.error('Error getting next display order:', error);
    return 1;
  }
};

/**
 * Compact display orders after user deletion
 * This ensures there are no gaps in the display order sequence
 * @param {string} deletedUserId - ID of the deleted user
 * @returns {Promise<boolean>} Success status
 */
const compactDisplayOrders = async (deletedUserId) => {
  try {
    // Use the Lambda function for efficient compaction
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'compactDisplayOrders',
          deletedUserId: deletedUserId
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (!result?.success) {
      console.error('Failed to compact display orders:', result?.error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error compacting display orders:', error);
    return false;
  }
};

/**
 * Validate and fix display order consistency
 * This function ensures all users have proper sequential display orders
 * @returns {Promise<boolean>} Success status
 */
export const validateAndFixDisplayOrders = async () => {
  try {
    // Use the Lambda function for validation and fixing
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'validateAndFixDisplayOrders'
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (!result?.success) {
      console.error('Failed to validate and fix display orders:', result?.error);
      return false;
    }

    console.log('Display orders validated and fixed successfully');
    return true;
  } catch (error) {
    console.error('Error validating display orders:', error);
    return false;
  }
};

export const apiGetUserLoginHistory = async (userId) => {
  try {
    const userResponse = await apiGetUser(userId);
    if (!userResponse.success) {
      return userResponse;
    }
    
    const loginHistory = userResponse.data?.data?.loginHistory || [];
    const lastTimeLogin = userResponse.data?.data?.lastTimeLogin;
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    
    let formattedHistory = loginHistory
      .filter(entry => entry.time && !isNaN(Number(entry.time)) && Number(entry.time) >= thirtyDaysAgo)
      .map(entry => ({
        timestamp: Number(entry.time),
        success: true
      }));
    
    // If no loginHistory but has lastTimeLogin within 30 days, include it
    if (formattedHistory.length === 0 && lastTimeLogin && !isNaN(Number(lastTimeLogin)) && Number(lastTimeLogin) >= thirtyDaysAgo) {
      formattedHistory = [{
        timestamp: Number(lastTimeLogin),
        success: true
      }];
    }
    
    return {
      success: true,
      data: formattedHistory
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};
